/**
 * stepper-nav.component.ts
 * ------------------------
 * Context-aware horizontal step navigation bar.
 * Rendered BELOW the header on all pages EXCEPT the landing page.
 *
 * Behaviour:
 *  - Active step  : bright white text, full opacity  (user is here)
 *  - Future step  : dimmed text, no pointer cursor   (not yet accessible)
 *  - Complete step: slightly dimmed but still clickable (user can go back)
 *  - Separator ">" between steps
 *
 * The steps shown depend on the flow mode from SessionService:
 *  - new      : Document Upload > Category Selection > Result and Analysis
 *  - existing : Application Details > Update Result and Analysis
 */

import { Component, OnInit, computed } from '@angular/core';
import { CommonModule }                 from '@angular/common';
import { Router, NavigationEnd }        from '@angular/router';
import { filter }                       from 'rxjs/operators';
import { SessionService }               from '../../core/services/session.service';

/** Maps route path segments to step keys */
const ROUTE_TO_STEP: Record<string, string> = {
  'document-upload':    'document-upload',
  'category-review':    'category-review',
  'assessment-report':  'assessment-report',
  'application-details':'application-details',
};

@Component({
  selector: 'app-stepper-nav',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './stepper-nav.component.html',
  styleUrl: './stepper-nav.component.scss',
})
export class StepperNavComponent implements OnInit {
  /** Steps to display — derived from current flow mode */
  steps = computed(() => this.sessionService.getStepsForFlow());

  /** The step key that matches the current URL */
  activeStepKey = '';

  constructor(
    private readonly router:         Router,
    private readonly sessionService: SessionService,
  ) {}

  ngOnInit(): void {
    // Keep activeStepKey in sync with the router
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e) => {
        const nav = e as NavigationEnd;
        this.activeStepKey = this.resolveStepKey(nav.urlAfterRedirects);
      });

    // Resolve on first render
    this.activeStepKey = this.resolveStepKey(this.router.url);
  }

  /**
   * Determines the visual state of a step.
   * @returns 'active' | 'complete' | 'locked'
   */
  getStepState(key: string): 'active' | 'complete' | 'locked' {
    if (key === this.activeStepKey) return 'active';
    if (this.sessionService.isStepComplete(key as any)) return 'complete';
    return 'locked';
  }

  /**
   * Navigates to a step — only allowed if step is active or complete.
   * Locked (future) steps are not clickable.
   */
  navigateTo(key: string): void {
    const state = this.getStepState(key);
    if (state === 'locked') return;
    this.router.navigate(['/', key]);
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  private resolveStepKey(url: string): string {
    // Strip leading slash and query params
    const segment = url.split('?')[0].replace(/^\//, '');
    return ROUTE_TO_STEP[segment] ?? '';
  }
}
